/**
 * Email signup popup — Broadcast theme
 * -----------------------------------------------------------------
 * Shows a two-column accessible modal after the visitor either
 * scrolls for N seconds or spends N seconds on the site (whichever
 * happens first). Skips entirely if the visitor already dismissed
 * it (1 day cookie, configurable) or already subscribed (permanent
 * cookie).
 *
 * Submission is handled via fetch so the popup swaps to its
 * success/error state in place, without a full page reload — this
 * mirrors how the theme's own newsletter-form.liquid behaves.
 * `form.posted_successfully?` on the section is also still checked
 * on load (via the `data-just-subscribed` attribute) as a fallback
 * for visitors without working JS, or if a submission gets
 * challenged (e.g. Shopify's spam/reCAPTCHA check) and the browser
 * ends up doing a real navigation instead.
 */
(function () {
  'use strict';

  var DISMISS_COOKIE = 'email_popup_dismissed';
  var SUBSCRIBE_COOKIE = 'email_popup_subscribed';
  var SUBSCRIBE_COOKIE_DAYS = 365;

  function getCookie(name) {
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(init);

  function init() {
    var popup = document.querySelector('[data-email-popup]');
    if (!popup) return;

    // The section renders this attribute only when Shopify's customer
    // form on this page just posted successfully (fallback path — see
    // file header comment).
    if (popup.dataset.justSubscribed === 'true') {
      setCookie(SUBSCRIBE_COOKIE, '1', SUBSCRIBE_COOKIE_DAYS);
      openPopup(popup, { skipTriggers: true });
      return;
    }

    if (getCookie(SUBSCRIBE_COOKIE) || getCookie(DISMISS_COOKIE)) {
      return;
    }

    var scrollDelay = (parseFloat(popup.dataset.scrollDelay) || 10) * 1000;
    var timeDelay = (parseFloat(popup.dataset.timeDelay) || 30) * 1000;

    var triggered = false;
    var scrollTimer = null;
    var timeTimer = null;

    function trigger() {
      if (triggered) return;
      triggered = true;
      clearTimeout(scrollTimer);
      clearTimeout(timeTimer);
      window.removeEventListener('scroll', onScroll);
      openPopup(popup);
    }

    function onScroll() {
      if (scrollTimer) return;
      window.removeEventListener('scroll', onScroll);
      scrollTimer = setTimeout(trigger, scrollDelay);
    }

    timeTimer = setTimeout(trigger, timeDelay);
    window.addEventListener('scroll', onScroll, { passive: true });

    wireDismissControls(popup);
    wireFormSubmission(popup);
  }

  function wireDismissControls(popup) {
    var closeButtons = popup.querySelectorAll('[data-email-popup-close]');
    var overlay = popup.querySelector('[data-email-popup-overlay]');
    var dismissDays = parseFloat(popup.dataset.dismissDays) || 1;

    function dismiss() {
      setCookie(DISMISS_COOKIE, '1', dismissDays);
      closePopup(popup);
    }

    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', dismiss);
    });

    if (overlay) {
      overlay.addEventListener('click', dismiss);
    }

    popup._emailPopupDismiss = dismiss;
  }

  // Submits the popup's newsletter form over fetch instead of letting
  // the browser do a full-page POST + reload. Uses event delegation
  // (bound once, on the popup shell) rather than binding directly to
  // the <form>, because the form node gets replaced by swapPopupContent
  // on every submit — a direct listener would only ever fire once.
  function wireFormSubmission(popup) {
    popup.addEventListener('submit', function (event) {
      var form = event.target.closest('#email-popup-form');
      if (!form) return;

      event.preventDefault();
      submitPopupForm(popup, form);
    });
  }

  function submitPopupForm(popup, form) {
    var submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) submitButton.disabled = true;

    fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'text/html' }
    })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshContent = doc.querySelector('#email-popup .email-popup__content');

        if (!freshContent) {
          // Couldn't find the section in the response — most likely a
          // spam/reCAPTCHA challenge page came back instead of the
          // normal markup. Fall back to a real submission rather than
          // failing silently.
          form.submit();
          return;
        }

        swapPopupContent(popup, freshContent);

        var freshPopup = doc.querySelector('#email-popup');
        if (freshPopup && freshPopup.dataset.justSubscribed === 'true') {
          setCookie(SUBSCRIBE_COOKIE, '1', SUBSCRIBE_COOKIE_DAYS);
        }
      })
      .catch(function () {
        if (submitButton) submitButton.disabled = false;
      });
  }

  // Replaces the popup's heading/blurb/form-or-success block with a
  // freshly rendered version (from either a successful submission or
  // a validation error), and re-announces/refocuses it for
  // accessibility the same way a fresh popup open would.
  function swapPopupContent(popup, freshContent) {
    var currentContent = popup.querySelector('.email-popup__content');
    if (!currentContent) return;

    currentContent.replaceWith(freshContent);

    var announcer = document.querySelector('[data-email-popup-announcer]');
    var success = freshContent.querySelector('.email-popup__success');
    var errorBox = freshContent.querySelector('.email-popup__error');
    var heading = freshContent.querySelector('#email-popup-heading');

    var announceText = '';
    if (success) {
      announceText = success.textContent.trim();
    } else if (errorBox) {
      announceText = errorBox.textContent.trim();
    } else if (heading) {
      announceText = heading.textContent.trim();
    }
    if (announcer) announcer.textContent = announceText;

    if (success) {
      success.setAttribute('tabindex', '-1');
      success.focus();
    } else if (errorBox) {
      errorBox.setAttribute('tabindex', '-1');
      errorBox.focus();
    } else if (heading) {
      heading.focus();
    }
  }

  var lastFocusedElement = null;

  function openPopup(popup, options) {
    options = options || {};

    // In case this is the "just subscribed" render, close controls and
    // the submit handler still need to be wired so the visitor can
    // dismiss the confirmation (the form itself won't be present).
    if (!popup._emailPopupDismiss) {
      wireDismissControls(popup);
    }
    if (!popup._emailPopupFormWired) {
      wireFormSubmission(popup);
      popup._emailPopupFormWired = true;
    }

    lastFocusedElement = document.activeElement;

    popup.hidden = false;
    document.body.style.overflow = 'hidden';

    var mainContent = document.querySelector('#MainContent, main');
    if (mainContent && 'inert' in mainContent) {
      mainContent.inert = true;
    } else if (mainContent) {
      mainContent.setAttribute('aria-hidden', 'true');
    }

    var dialog = popup.querySelector('[data-email-popup-dialog]');
    var heading = popup.querySelector('#email-popup-heading');
    var blurb = popup.querySelector('#email-popup-blurb');
    var announcer = document.querySelector('[data-email-popup-announcer]');

    // Speak the heading + offer copy explicitly via a live region. Tabbing
    // only ever lands on focusable controls (the email field, buttons),
    // so plain paragraph text like the blurb never gets read that way —
    // and aria-describedby support for dialogs is inconsistent across
    // screen readers. The live region guarantees the offer is heard the
    // moment the popup opens, regardless of how the visitor navigates
    // afterward.
    if (announcer) {
      var parts = [];
      if (heading) parts.push(heading.textContent.trim());
      if (blurb) parts.push(blurb.textContent.trim());
      announcer.textContent = parts.join('. ');
    }

    // Move focus into the dialog itself (not a specific field) so
    // assistive tech treats it as a modal and its accessible name/
    // description are associated correctly. Wait a tick so the just-
    // unhidden dialog is in the accessibility tree first.
    window.requestAnimationFrame(function () {
      if (dialog) {
        dialog.focus();
      } else if (heading) {
        heading.focus();
      }
    });

    document.addEventListener('keydown', onKeydown);

    popup._focusTrapCleanup = trapFocus(dialog);
  }

  function closePopup(popup) {
    popup.hidden = true;
    document.body.style.overflow = '';

    var mainContent = document.querySelector('#MainContent, main');
    if (mainContent && 'inert' in mainContent) {
      mainContent.inert = false;
    } else if (mainContent) {
      mainContent.removeAttribute('aria-hidden');
    }

    document.removeEventListener('keydown', onKeydown);

    if (typeof popup._focusTrapCleanup === 'function') {
      popup._focusTrapCleanup();
    }

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  function onKeydown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    var popup = document.querySelector('[data-email-popup]:not([hidden])');
    if (popup && typeof popup._emailPopupDismiss === 'function') {
      popup._emailPopupDismiss();
    }
  }

  function trapFocus(container) {
    if (!container) return function () {};

    var focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function handleTab(event) {
      if (event.key !== 'Tab') return;

      var focusable = Array.prototype.slice.call(container.querySelectorAll(focusableSelector));
      focusable = focusable.filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handleTab);
    return function cleanup() {
      container.removeEventListener('keydown', handleTab);
    };
  }
})();